import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Code, Zap, Smartphone, Layout as LayoutIcon, TrendingUp, Shield, CheckCircle2, Play, Users, Star, ArrowUpRight, Cpu, Globe, ShoppingBag, Database, Video, Cloud, Bot, Fingerprint, Sparkles, Megaphone, Terminal, Activity, Layers, ChevronLeft, ChevronRight, Quote, Search, PenTool, Rocket, Lock, Server, Box, Camera, Monitor, Radio, Bitcoin, BarChart, MessageSquare, Palette } from 'lucide-react';
import { Section, GlassContainer, GradientText, Badge, PrimaryButton, ShineButton, OptimizedImage, OptimizedAvatar, SectionHeader, SplitText, Marquee, Accordion, OutlineButton } from '../components/UI';
import { storage } from '../utils/storage';
import { getApprovedTestimonials } from '../utils/testimonialService';
import { getPortfolio } from '../utils/portfolioService';
import { Testimonial, Project } from '../types';
import { useSEO, PAGE_SEO, addStructuredData } from '../utils/seo';
import { generateFAQSchema } from '../utils/schema-generator';
import { useIsMobile } from '../utils/performance';

// --- INTERACTIVE PARTICLE CANVAS COMPONENT ---
const GravityParticles = ({ isMobile }: { isMobile: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas to full window size for the background effect
    let width = window.innerWidth;
    let height = window.innerHeight; // or parent height

    let canvasRect = canvas.getBoundingClientRect();

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        width = parent.offsetWidth;
        height = parent.offsetHeight;
        canvas.width = width;
        canvas.height = height;
        canvasRect = canvas.getBoundingClientRect();
      }
    };

    resizeCanvas();

    const particles: Particle[] = [];
    const particleCount = isMobile ? 30 : 80;
    const colors = ['#008aff', '#003a6b', '#94a3b8', '#cbd5e1'];

    let mouse = { x: -1000, y: -1000 };

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      color: string;
      density: number;

      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.8; // Slower, floatier movement
        this.vy = (Math.random() - 0.5) * 0.8;
        this.size = 3.1;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.density = (Math.random() * 30) + 1;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around edges for infinite feeling
        if (this.x < 0) this.x = width;
        if (this.x > width) this.x = 0;
        if (this.y < 0) this.y = height;
        if (this.y > height) this.y = 0;

        // Mouse Interaction (Repulsion)
        let dx = mouse.x - this.x;
        let dy = mouse.y - this.y;
        let distance = Math.sqrt(dx * dx + dy * dy);
        let maxDistance = 300; // Larger interaction radius

        if (distance < maxDistance) {
          const forceDirectionX = dx / distance;
          const forceDirectionY = dy / distance;
          const force = (maxDistance - distance) / maxDistance;
          const directionX = forceDirectionX * force * this.density * 0.6; // Soft repulsion
          const directionY = forceDirectionY * force * this.density * 0.6;

          this.x -= directionX;
          this.y -= directionY;
        }
      }
    }

    const init = () => {
      particles.length = 0;
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw Connections
      for (let a = 0; a < particles.length; a++) {
        for (let b = a; b < particles.length; b++) {
          let dx = particles[a].x - particles[b].x;
          let dy = particles[a].y - particles[b].y;
          let distance = dx * dx + dy * dy;

          if (distance < 15000) {
            let opacityValue = 1 - (distance / 15000);
            ctx.strokeStyle = `rgba(148, 163, 184, ${opacityValue * 0.12})`; // Visible connection lines
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particles[a].x, particles[a].y);
            ctx.lineTo(particles[b].x, particles[b].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        p.draw();
        p.update();
      });

      requestAnimationFrame(animate);
    };

    init();
    animate();

    // Track mouse globally relative to the canvas - Cached position to avoid repeated reflows
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX - canvasRect.left;
      mouse.y = e.clientY - canvasRect.top;
    };

    window.addEventListener('resize', () => { resizeCanvas(); init(); });
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return <canvas ref={canvasRef} className="block w-full h-full will-change-transform opacity-60" />;
};

const HERO_SERVICES = [
  { name: "Website & Web App Development", icon: Globe },
  { name: "UI/UX & Creative Design", icon: Palette },
  { name: "Mobile App Development", icon: Smartphone },
  { name: "AI & Automation Services", icon: Bot },
  { name: "Digital Marketing & Growth", icon: TrendingUp },
  { name: "Branding & Creative Content", icon: Fingerprint },
  { name: "E-Commerce Growth", icon: ShoppingBag },
  { name: "Video & Animation", icon: Video },
  { name: "Influencer Solutions", icon: Users }
];

const SERVICE_CATALOG = [
  {
    id: 'web',
    category: "Website & Web App Development",
    icon: Globe,
    desc: "Scalable, high-performance web solutions.",
    items: [
      { name: "Custom Website Development", icon: Globe },
      { name: "Enterprise Web Applications", icon: Server },
      { name: "E-Commerce Store Development", icon: ShoppingBag },
      { name: "CMS Development (WordPress / Headless)", icon: LayoutIcon },
      { name: "Landing Page Design & Optimization", icon: Layers },
      { name: "Web Portal Development", icon: Box },
      { name: "Website Speed Optimization", icon: Zap },
      { name: "Website Security & Hardening", icon: Shield }
    ]
  },
  {
    id: 'design',
    category: "UI/UX & Creative Design",
    icon: LayoutIcon,
    desc: "Award-winning aesthetics meet user psychology.",
    items: [
      { name: "UI/UX Design (Web + Mobile)", icon: LayoutIcon },
      { name: "Brand Identity & Logo Design", icon: Fingerprint },
      { name: "Product Design", icon: Box },
      { name: "Wireframing & Prototyping", icon: PenTool },
      { name: "Motion Graphics & Animations", icon: Video },
      { name: "3D Graphics & Visualizations", icon: Box },
      { name: "Design System Creation", icon: Layers },
      { name: "Micro-Interactions Design", icon: Sparkles }
    ]
  },
  {
    id: 'mobile',
    category: "Mobile App Development",
    icon: Smartphone,
    desc: "Native and cross-platform mobile excellence.",
    items: [
      { name: "Android App Development (Kotlin)", icon: Smartphone },
      { name: "iOS App Development (Swift)", icon: Smartphone },
      { name: "Cross-Platform Apps (Flutter)", icon: Layers },
      { name: "Hybrid App Development", icon: Globe },
      { name: "App UI/UX Design", icon: LayoutIcon },
      { name: "App Store Optimization", icon: Search },
      { name: "App Maintenance & Support", icon: Activity }
    ]
  },
  {
    id: 'ai',
    category: "AI & Automation Services",
    icon: Bot,
    desc: "Next-gen intelligence for your business.",
    items: [
      { name: "AI Chatbot Development", icon: Bot },
      { name: "AI Trading Bots", icon: TrendingUp },
      { name: "AI Image/Video Processing Tools", icon: Video },
      { name: "Machine Learning Models", icon: Cpu },
      { name: "Data Analytics & Predictions", icon: Activity },
      { name: "Process Automation Systems", icon: Zap },
      { name: "AI Recommendation Systems", icon: Star },
      { name: "NLP-Based Applications", icon: MessageSquare },
      { name: "OCR Solutions", icon: Search }
    ]
  },
  {
    id: 'marketing',
    category: "Digital Marketing & Growth",
    icon: TrendingUp,
    desc: "Data-driven strategies to dominate markets.",
    items: [
      { name: "SEO (Technical, On/Off-Page)", icon: Search },
      { name: "Social Media Marketing (SMM)", icon: Users },
      { name: "Google Ads Management", icon: Megaphone },
      { name: "Meta Ads Management", icon: Megaphone },
      { name: "Email Marketing Automation", icon: Radio },
      { name: "Lead Generation Campaigns", icon: Users },
      { name: "Content Marketing", icon: PenTool },
      { name: "CRO (Conversion Rate Opt.)", icon: BarChart },
      { name: "Digital PR Services", icon: Globe }
    ]
  },
  {
    id: 'branding',
    category: "Branding & Creative Content",
    icon: Fingerprint,
    desc: "Stories that stick and identities that matter.",
    items: [
      { name: "Brand Strategy + Identity", icon: PenTool },
      { name: "Company Profile Design", icon: Layers },
      { name: "Social Media Creative Designs", icon: Camera },
      { name: "Commercial Ads Production", icon: Video },
      { name: "Copywriting & Scriptwriting", icon: PenTool },
      { name: "Photography & Product Shoots", icon: Camera }
    ]
  },
  {
    id: 'ecommerce',
    category: "E-Commerce Growth",
    icon: ShoppingBag,
    desc: "End-to-end retail and shopping solutions.",
    items: [
      { name: "Shopify Store Setup", icon: ShoppingBag },
      { name: "WooCommerce Development", icon: ShoppingBag },
      { name: "Amazon Product Listing", icon: Box },
      { name: "Amazon PPC Management", icon: Megaphone },
      { name: "Ecommerce Automation Tools", icon: Zap },
      { name: "Conversion Funnel Optimization", icon: BarChart },
      { name: "Product Packaging Design", icon: Box }
    ]
  },
  {
    id: 'video',
    category: "Video & Animation",
    icon: Video,
    desc: "Captivating visuals in motion.",
    items: [
      { name: "2D/3D Animation", icon: Video },
      { name: "Explainer Videos", icon: Monitor },
      { name: "Motion Graphics", icon: Layers },
      { name: "Product Animation", icon: Box },
      { name: "Corporate Video Production", icon: Users },
      { name: "Short Ads (TikTok/Reels)", icon: Smartphone }
    ]
  },
  {
    id: 'influencer',
    category: "Influencer Solutions",
    icon: Users,
    desc: "Leveraging the power of social voices.",
    items: [
      { name: "Influencer Marketing Campaigns", icon: Users },
      { name: "UGC Content Creation", icon: Camera },
      { name: "Social Account Management", icon: Megaphone },
      { name: "Full Social Media Branding", icon: PenTool },
      { name: "Reputation Management", icon: Shield }
    ]
  },
  {
    id: 'custom',
    category: "Need Something Else?",
    icon: MessageSquare,
    desc: "Can't find what you need? Let's discuss your custom requirements.",
    items: [
      { name: "Custom Software Development", icon: Code },
      { name: "Consultation & Strategy", icon: Sparkles },
      { name: "Technical Support & Maintenance", icon: Shield },
      { name: "Training & Workshops", icon: Users },
      { name: "Other Services - Contact Us", icon: MessageSquare }
    ]
  }
];

const PROCESS_STEPS = [
  {
    num: "01", title: "Discovery",
    desc: "We dive deep into your business goals, user needs, and market landscape to build a solid strategy.",
    icon: Search
  },
  {
    num: "02", title: "Design",
    desc: "Our award-winning designers craft intuitive, stunning interfaces that align with your brand identity.",
    icon: PenTool
  },
  {
    num: "03", title: "Development",
    desc: "We build scalable, secure, and high-performance solutions using the latest tech stack.",
    icon: Code
  },
  {
    num: "04", title: "Launch & Scale",
    desc: "Rigorous testing followed by a smooth deployment. We stay with you to optimize and grow.",
    icon: Rocket
  }
];

const SELECTED_PROJECTS = [
  {
    title: "Nexus Finance",
    category: "FinTech Platform",
    desc: "A decentralized trading dashboard handling $2B+ in quarterly volume. Built for speed, security, and real-time data visualization.",
    tech: [
      { name: "React", icon: Code },
      { name: "Node.js", icon: Server },
      { name: "AWS", icon: Cloud },
      { name: "PostgreSQL", icon: Database }
    ],
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
  },
  {
    title: "Aura Commerce",
    category: "E-Commerce",
    desc: "Immersive 3D shopping experience for a high-end streetwear brand. Features AR try-on and AI recommendations.",
    tech: [
      { name: "Shopify", icon: ShoppingBag },
      { name: "WebGL", icon: Box },
      { name: "Three.js", icon: Layers },
      { name: "Redis", icon: Database }
    ],
    img: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=1200"
  },
  {
    title: "Synapse AI",
    category: "AI & Data",
    desc: "AI-powered diagnostic tool for medical professionals. Processing medical imaging with 99.9% accuracy using Computer Vision.",
    tech: [
      { name: "Python", icon: Terminal },
      { name: "TensorFlow", icon: Cpu },
      { name: "Docker", icon: Box },
      { name: "React", icon: Code }
    ],
    img: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1200"
  }
];

const FAQS = [
  { title: "How long does a typical project take?", content: "Timelines vary by scope. A branding project might take 2-4 weeks, while a custom web app can take 3-6 months. We provide detailed roadmaps during discovery." },
  { title: "Do you work with startups?", content: "Absolutely. We love helping founders build their MVPs and scale. We also have specific packages tailored for high-growth startups." },
  { title: "What is your pricing model?", content: "We offer both project-based fixed pricing and monthly retainers for ongoing support and growth marketing." },
  { title: "Do you provide post-launch support?", content: "Yes! We believe launch is just the beginning. We offer maintenance packages to ensure your digital product remains secure and up-to-date." }
];

const NESCOTECH_FEATURES = [
  {
    id: 0,
    title: "Web & App Development",
    desc: "We build custom websites, enterprise applications, e-commerce stores, and mobile apps for iOS, Android, and cross-platform using React, Next.js, Flutter, and more.",
    icon: Globe,
    color: "from-brand-lime to-green-400"
  },
  {
    id: 1,
    title: "AI & Automation",
    desc: "From custom AI chatbots and trading bots to machine learning models and process automation — we make your business smarter and faster.",
    icon: Bot,
    color: "from-purple-500 to-pink-500"
  },
  {
    id: 2,
    title: "Digital Marketing",
    desc: "Data-driven SEO, Google & Meta Ads, social media marketing, email automation, and lead generation to dominate your market and scale.",
    icon: TrendingUp,
    color: "from-blue-500 to-cyan-400"
  },
  {
    id: 3,
    title: "Creative & Branding",
    desc: "Award-winning UI/UX design, brand identity, motion graphics, video production, and 3D visuals that make your brand unforgettable.",
    icon: PenTool,
    color: "from-orange-500 to-brand-yellow"
  }
];

export const Home = () => {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(SERVICE_CATALOG[0]);
  const [selectedItem, setSelectedItem] = useState<{ name: string; icon: any } | null>(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [projectIndex, setProjectIndex] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const isMobile = useIsMobile(); // For performance - disable heavy animations on mobile

  // Load testimonials from Firebase
  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        // First try to get from Firebase
        const firebaseTestimonials = await getApprovedTestimonials();
        if (firebaseTestimonials.length > 0) {
          setTestimonials(firebaseTestimonials);
        } else {
          // Fallback to localStorage demo data if Firebase is empty
          const demoTestimonials = storage.getTestimonials();
          setTestimonials(demoTestimonials.filter(t => t.status === 'active' || t.status === 'approved'));
        }
      } catch (error) {
        console.error('Error loading testimonials:', error);
        // Fallback on error
        const demoTestimonials = storage.getTestimonials();
        setTestimonials(demoTestimonials.filter(t => t.status === 'active' || t.status === 'approved'));
      }
    };
    loadTestimonials();
    window.addEventListener('data-change', loadTestimonials);
    return () => window.removeEventListener('data-change', loadTestimonials);
  }, []);

  // Load portfolio projects from Firebase
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const firebaseProjects = await getPortfolio();
        if (firebaseProjects.length > 0) {
          setProjects(firebaseProjects);
        } else {
          // Fallback to localStorage demo data if Firebase is empty
          setProjects(storage.getProjects());
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects(storage.getProjects());
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials]);

  const nextTestimonial = () => setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const nextProject = () => projects.length > 0 && setProjectIndex((prev) => (prev + 1) % projects.length);
  const prevProject = () => projects.length > 0 && setProjectIndex((prev) => (prev - 1 + projects.length) % projects.length);
  const currentProject = projects.length > 0 ? projects[projectIndex] : null;

  // SEO Meta Tags
  useSEO(PAGE_SEO.home);

  // FAQ Structured Data
  const faqSchema = generateFAQSchema(FAQS.map(faq => ({
    question: faq.title,
    answer: faq.content
  })));
  addStructuredData(faqSchema);

  return (
    <>
      {/* 1. HERO SECTION REDESIGNED */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28 pb-20">

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 -z-30 bg-[radial-gradient(#94a3b8_1px,transparent_1px)] [background-size:40px_40px] opacity-[0.2]" />

        {/* INTERACTIVE ANTI-GRAVITY CANVAS - Only on Desktop */}
        {/* BACKGROUND INTERACTIVE CANVAS - Throttled for mobile */}
        <div className="absolute inset-0 z-0 opacity-40 pointer-events-none">
          <GravityParticles isMobile={isMobile} />
        </div>


        {/* Dynamic Background Blobs - Subtle behind particles */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-brand-lime/5 rounded-full mix-blend-multiply filter blur-[100px] animate-float opacity-30" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] bg-brand-yellow/5 rounded-full mix-blend-multiply filter blur-[100px] animate-float-delayed opacity-30" />
        </div>

        <div className="max-w-5xl mx-auto w-full relative z-10 px-6 text-center">

          {/* Service Pills Marquee */}
          <div className="overflow-hidden w-full mb-8 mask-gradient-sides">
            <div className="flex animate-[scroll_20s_linear_infinite] gap-4 w-max">
              {[...Array(2)].map((_, repeat) => (
                <div key={repeat} className="flex gap-4">
                  {[
                    { label: "Web Development", icon: <Globe size={14} /> },
                    { label: "Mobile Apps", icon: <Smartphone size={14} /> },
                    { label: "AI Solutions", icon: <Sparkles size={14} /> },
                    { label: "Branding", icon: <Palette size={14} /> },
                    { label: "Cloud Infrastructure", icon: <Cloud size={14} /> },
                    { label: "UI/UX Design", icon: <Layers size={14} /> },
                  ].map((tag, i) => (
                    <span key={i} className="flex items-center gap-2 px-5 py-2 rounded-full bg-white/50 border border-white/60 text-xs font-bold uppercase tracking-wider text-gray-700 backdrop-blur-md shadow-sm whitespace-nowrap hover:bg-white/80 transition-colors">
                      <span className="text-brand-lime">{tag.icon}</span>
                      {tag.label}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-7xl lg:text-9xl font-extrabold tracking-tight text-gray-900 leading-[1.1] sm:leading-[1] mb-8">
            <div className="overflow-hidden flex justify-center">
              <SplitText delay={0.2} className="block">Architecting</SplitText>
            </div>
            <div className="overflow-hidden flex justify-center items-baseline gap-4 flex-wrap">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-lime to-brand-yellow animate-text-reveal" style={{ animationDelay: '0.4s' }}>Digital</span>
              <SplitText delay={0.6} className="block opacity-60">Ecosystems</SplitText>
            </div>
          </h1>

          {/* Subtext */}
          <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up mb-12 font-medium" style={{ animationDelay: '0.8s' }}>
            We are a full-service agency merging <strong>creative vision</strong> with <strong>engineering precision</strong> to build liquid-smooth digital products that scale.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-6 animate-fade-in-up" style={{ animationDelay: '1s' }}>
            <ShineButton onClick={() => navigate('/booking')} className="!px-12 !py-5 text-xl shadow-2xl shadow-brand-lime/30 hover:scale-105 transition-transform">
              Start Project
            </ShineButton>
            <button
              onClick={() => navigate('/services')}
              className="group flex items-center justify-center gap-3 font-bold text-gray-700 hover:text-gray-900 px-8 py-5 transition-all bg-white/30 backdrop-blur-md border border-white/50 rounded-full hover:bg-white/60"
            >
              View Services <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Trust Indicator */}
          <div className="pt-12 flex flex-col items-center gap-4 animate-fade-in-up" style={{ animationDelay: '1.2s' }}>
            <div className="flex -space-x-3" role="group" aria-label="Client testimonials avatars">
              {[1, 2, 3, 4].map(i => (
                <OptimizedAvatar
                  key={i}
                  src={`https://i.pravatar.cc/48?img=${i + 10}`}
                  alt={`Client avatar ${i}`}
                  width={48}
                  height={48}
                  className="w-12 h-12 border-2 border-white shadow-md"
                />
              ))}
            </div >
            <div className="text-sm bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full border border-white/50 shadow-sm">
              <span className="font-bold text-gray-900">250+</span> Happy Clients <span className="mx-2 text-gray-300">|</span> <span className="text-brand-yellow font-bold">★★★★★</span>
            </div>
          </div >

        </div >
      </section >

      {/* 2. SERVICES MARQUEE */}
      < div className="hidden md:block py-8 bg-white/30 border-y border-white/50 backdrop-blur-sm overflow-hidden" >
        <Marquee>
          {HERO_SERVICES.map((service, i) => (
            <div
              key={i}
              className="flex-shrink-0 group flex items-center gap-3 mx-6 cursor-default transition-all duration-300 hover:scale-105"
            >
              <div className="p-2.5 rounded-xl bg-gray-50/50 border border-gray-200/50 text-gray-400 group-hover:text-brand-lime group-hover:bg-white group-hover:shadow-md transition-all">
                <service.icon size={18} />
              </div>
              <span className="text-base font-semibold text-slate-400 group-hover:text-slate-900 transition-colors whitespace-nowrap">
                {service.name}
              </span>
            </div>
          ))}
        </Marquee>
      </div >

      {/* 3. NEW COMPREHENSIVE SERVICE CATALOG SECTION */}
      < Section className="bg-gradient-to-b from-white/0 to-white/60" >
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <Badge>Full-Service Platform</Badge>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-6 mb-6">
            Everything you need to <GradientText>Scale</GradientText>
          </h2>
          <p className="text-xl text-gray-500">
            We are more than just a dev shop. We are a complete digital ecosystem offering everything from AI automation to high-end branding.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left: Category Sidebar */}
          <div className="lg:col-span-4 flex flex-row lg:flex-col gap-2 lg:sticky lg:top-24 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 no-scrollbar snap-x">
            {SERVICE_CATALOG.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveService(cat); setSelectedItem(null); }}
                className={`
                   text-left px-5 py-3 lg:px-6 lg:py-4 rounded-xl transition-all duration-300 flex items-center justify-between group whitespace-nowrap lg:whitespace-normal snap-start
                   ${activeService.id === cat.id
                    ? 'bg-brand-dark text-white shadow-lg shadow-brand-dark/20 scale-105 z-10'
                    : 'bg-white/40 hover:bg-white text-gray-600 hover:text-brand-dark hover:shadow-md'}
                 `}
              >
                <div className="flex items-center gap-3">
                  <cat.icon size={18} className={activeService.id === cat.id ? 'text-brand-lime' : 'text-gray-400 group-hover:text-brand-dark'} />
                  <span className="font-bold text-xs md:text-sm lg:text-base">{cat.category}</span>
                </div>
                <ArrowRight size={14} className={`hidden lg:block transition-all ${activeService.id === cat.id ? 'text-brand-lime opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`} />
              </button>
            ))}
          </div>

          {/* Right: Detail Grid */}
          <div className="lg:col-span-8 min-h-[400px] lg:min-h-[600px]">
            <div className="relative">
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-lime/10 rounded-full blur-3xl -z-10" />

              <div className="mb-8 p-8 rounded-3xl bg-gray-900/95 border border-white/10 backdrop-blur-xl text-white shadow-2xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-brand-lime/20 rounded-xl text-brand-lime">
                    <activeService.icon size={32} />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-white">{activeService.category}</h3>
                    <p className="text-gray-400">{activeService.desc}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in-up">
                {activeService.items.map((item, idx) => (
                  <GlassContainer
                    key={idx}
                    hoverEffect
                    className={`p-5 flex items-start gap-3 group cursor-pointer transition-all duration-300 ${selectedItem?.name === item.name
                      ? 'bg-brand-lime/20 border-brand-lime ring-2 ring-brand-lime/30'
                      : 'bg-white/50'
                      }`}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className={`mt-1 min-w-[32px] w-8 h-8 rounded-lg flex items-center justify-center shadow-sm transition-all duration-300 ${selectedItem?.name === item.name
                      ? 'bg-brand-lime text-white scale-110'
                      : 'bg-white text-gray-400 group-hover:text-brand-lime group-hover:scale-110'
                      }`}>
                      <item.icon size={18} />
                    </div>
                    <span className={`font-semibold leading-tight mt-1.5 transition-colors ${selectedItem?.name === item.name
                      ? 'text-gray-900'
                      : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                      {item.name}
                    </span>
                  </GlassContainer>
                ))}
              </div>

              <div className="mt-10 flex justify-center">
                <PrimaryButton
                  onClick={() => navigate('/booking', {
                    state: {
                      selectedService: {
                        name: selectedItem?.name || activeService.category,
                        desc: activeService.desc,
                        features: activeService.items.slice(0, 3).map(i => i.name)
                      }
                    }
                  })}
                  className="shadow-xl"
                >
                  Get Quote for {selectedItem?.name || activeService.category}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </Section >

      {/* 4. WHY CHOOSE NESCOTECH - REDESIGNED HOLOGRAPHIC SHOWCASE */}
      < Section className="relative overflow-hidden py-20 sm:py-32 bg-gray-900 text-white rounded-3xl sm:rounded-[3rem] mx-2 sm:mx-10 shadow-2xl shadow-black/20" >
        {/* Dark Mode Background Effects */}
        < div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl" >
          <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-lime/10 rounded-full blur-[120px] animate-pulse-slow" />
          <div className="hidden md:block absolute top-0 right-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />
          {/* Grid Mesh */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />
        </div >

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center px-4 lg:px-0">
          {/* LEFT: INTERACTIVE MENU */}
          <div className="text-center lg:text-left lg:pl-12">
            <Badge className="bg-brand-lime text-gray-900 border-brand-lime">The NescoTech Advantage</Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mt-6 mb-8 leading-tight">
              Engineering Precision meets <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-lime to-brand-yellow">Creative Chaos</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mb-12 max-w-md mx-auto lg:mx-0">
              We don't just build websites. We engineer digital assets that compound in value over time.
            </p>

            <div className="space-y-4">
              {NESCOTECH_FEATURES.map((feat) => (
                <div
                  key={feat.id}
                  onMouseEnter={() => setActiveFeature(feat.id)}
                  onClick={() => setActiveFeature(feat.id)}
                  className={`
                        group cursor-pointer p-4 sm:p-6 rounded-2xl transition-all duration-500 border border-transparent relative overflow-hidden
                        ${activeFeature === feat.id ? 'bg-white/10 border-white/20 shadow-lg' : 'hover:bg-white/5 hover:border-white/5'}
                      `}
                >
                  {/* Active Indicator Bar */}
                  {activeFeature === feat.id && (
                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${feat.color}`} />
                  )}

                  <div className="flex items-start gap-4">
                    <div className={`
                            p-3 rounded-xl transition-all duration-500
                            ${activeFeature === feat.id ? `bg-gradient-to-br ${feat.color} text-white shadow-lg` : 'bg-white/10 text-gray-400 group-hover:bg-white/20 group-hover:text-white'}
                         `}>
                      <feat.icon size={24} />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold mb-1 transition-colors ${activeFeature === feat.id ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                        {feat.title}
                      </h3>
                      <p className={`text-sm leading-relaxed transition-colors ${activeFeature === feat.id ? 'text-gray-300' : 'text-gray-500'}`}>
                        {feat.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: HOLOGRAPHIC VIEWPORT */}
          <div className="h-[400px] sm:h-[500px] lg:h-[600px] relative flex items-center justify-center px-4 lg:pr-12 lg:pl-0">
            <div className="w-full h-full relative">
              {/* Glass Pane Frame */}
              <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden flex items-center justify-center">

                {/* Internal Reflection */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none" />

                {/* DYNAMIC VISUALS BASED ON ACTIVE STATE */}
                <div className="relative z-10 w-full h-full flex items-center justify-center p-4 sm:p-8 lg:p-12 scale-[0.85] sm:scale-100">

                  {activeFeature === 0 && ( // Web & App Development - Orbiting Icons
                    <div className="relative w-full h-full flex items-center justify-center animate-fade-in-up">
                      {/* Outer Orbiting Ring - Large Icons */}
                      <div className="absolute w-[240px] h-[240px] sm:w-[280px] sm:h-[280px] animate-[spin_25s_linear_infinite]">
                        {/* Top - React */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/40 animate-[spin_25s_linear_infinite_reverse]">
                          <Code size={20} className="text-white" />
                        </div>
                        {/* Bottom - Next.js */}
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-11 h-11 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center shadow-lg border border-gray-600 animate-[spin_25s_linear_infinite_reverse]">
                          <Zap size={20} className="text-white" />
                        </div>
                        {/* Left - Flutter */}
                        <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-gradient-to-br from-blue-400 to-cyan-300 rounded-xl flex items-center justify-center shadow-lg shadow-blue-400/40 animate-[spin_25s_linear_infinite_reverse]">
                          <Smartphone size={20} className="text-white" />
                        </div>
                        {/* Right - Node.js */}
                        <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-gradient-to-br from-brand-lime to-green-500 rounded-xl flex items-center justify-center shadow-lg shadow-brand-lime/40 animate-[spin_25s_linear_infinite_reverse]">
                          <Server size={20} className="text-white" />
                        </div>
                      </div>

                      {/* Inner Orbiting Ring - Smaller Icons - Opposite Direction */}
                      <div className="absolute w-[180px] h-[180px] sm:w-[200px] sm:h-[200px] animate-[spin_18s_linear_infinite_reverse]">
                        {/* Top-Right */}
                        <div className="absolute top-[10%] right-[10%] w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center shadow-lg animate-[spin_18s_linear_infinite]">
                          <Terminal size={14} className="text-white" />
                        </div>
                        {/* Bottom-Left */}
                        <div className="absolute bottom-[10%] left-[10%] w-8 h-8 bg-gradient-to-br from-brand-yellow to-orange-400 rounded-lg flex items-center justify-center shadow-lg shadow-brand-yellow/30 animate-[spin_18s_linear_infinite]">
                          <Database size={14} className="text-white" />
                        </div>
                        {/* Top-Left */}
                        <div className="absolute top-[10%] left-[10%] w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg animate-[spin_18s_linear_infinite]">
                          <Cloud size={14} className="text-white" />
                        </div>
                        {/* Bottom-Right */}
                        <div className="absolute bottom-[10%] right-[10%] w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center shadow-lg animate-[spin_18s_linear_infinite]">
                          <Globe size={14} className="text-white" />
                        </div>
                      </div>

                      {/* Glow Rings - Centered */}
                      <div className="absolute w-[200px] h-[200px] sm:w-[240px] sm:h-[240px] border border-brand-lime/20 rounded-full" />
                      <div className="absolute w-[150px] h-[150px] sm:w-[180px] sm:h-[180px] border border-cyan-500/15 rounded-full" />

                      {/* Central Devices - Centered */}
                      <div className="relative flex items-center gap-3 z-20">
                        {/* Main Laptop */}
                        <div className="bg-gray-800/90 backdrop-blur-sm rounded-xl p-1.5 shadow-2xl border border-gray-700/50">
                          <div className="bg-gray-900 rounded-lg overflow-hidden w-32 h-24">
                            <div className="bg-gray-800 h-3.5 flex items-center px-1.5 gap-0.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                              <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            </div>
                            <div className="p-1.5 space-y-1">
                              <div className="h-2 bg-gradient-to-r from-brand-lime to-brand-yellow rounded w-3/4" />
                              <div className="h-1 bg-gray-700 rounded w-full" />
                              <div className="h-1 bg-gray-700 rounded w-2/3" />
                              <div className="flex gap-1 mt-1.5">
                                <div className="h-6 flex-1 bg-gray-800 rounded" />
                                <div className="h-6 flex-1 bg-gray-800 rounded" />
                              </div>
                            </div>
                          </div>
                          <div className="h-1 bg-gray-700 rounded-b mt-0.5 mx-2" />
                        </div>

                        {/* Phone */}
                        <div className="bg-gray-800/90 backdrop-blur-sm rounded-xl p-1 shadow-xl border border-gray-700/50 animate-float">
                          <div className="bg-gray-900 rounded-lg w-10 h-16 overflow-hidden">
                            <div className="h-1.5 bg-gray-800 flex justify-center items-center">
                              <div className="w-3 h-0.5 bg-gray-700 rounded-full" />
                            </div>
                            <div className="p-1 space-y-0.5">
                              <div className="h-0.5 bg-brand-lime rounded w-3/4" />
                              <div className="h-0.5 bg-gray-700 rounded" />
                              <div className="h-3 bg-gray-800 rounded mt-0.5" />
                              <div className="h-2 bg-gray-800 rounded" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Badge - Bottom Center */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-brand-lime/10 border border-brand-lime/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
                        <span className="w-1.5 h-1.5 bg-brand-lime rounded-full animate-pulse" />
                        <span className="text-brand-lime text-xs font-medium">Live • 99.9% Uptime</span>
                      </div>

                      {/* Background Glow */}
                      <div className="absolute -z-10 w-72 h-72 bg-brand-lime/10 rounded-full blur-3xl" />
                    </div>
                  )}

                  {activeFeature === 1 && ( // AI & Automation - Premium Neural Brain
                    <div className="relative w-full h-full flex items-center justify-center animate-fade-in-up">
                      {/* Central AI Brain */}
                      <div className="relative scale-90 sm:scale-100">
                        {/* Outer Rotating Ring */}
                        <div className="absolute inset-0 w-40 h-40 sm:w-48 sm:h-48 -m-4 sm:-m-6 border-2 border-dashed border-purple-500/30 rounded-full animate-[spin_20s_linear_infinite]" />
                        <div className="absolute inset-0 w-32 h-32 sm:w-40 sm:h-40 -m-0 sm:-m-2 border border-purple-400/40 rounded-full animate-[spin_15s_linear_infinite_reverse]" />

                        {/* Brain Core */}
                        <div className="relative w-36 h-36 bg-gradient-to-br from-purple-600 via-violet-600 to-fuchsia-600 rounded-full flex items-center justify-center shadow-2xl shadow-purple-500/50">
                          {/* Inner Glow */}
                          <div className="absolute inset-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full opacity-50 blur-sm animate-pulse" />

                          {/* AI Icon */}
                          <Bot size={48} className="text-white relative z-10 drop-shadow-lg" />

                          {/* Pulse Rings */}
                          <div className="absolute inset-0 border-2 border-purple-400/50 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
                          <div className="absolute inset-0 border border-pink-400/30 rounded-full animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />
                        </div>

                        {/* Neural Connection Nodes */}
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
                          <div
                            key={i}
                            className="absolute w-4 h-4 bg-purple-400 rounded-full shadow-lg shadow-purple-500/50"
                            style={{
                              top: `${50 + 42 * Math.sin((angle * Math.PI) / 180)}%`,
                              left: `${50 + 42 * Math.cos((angle * Math.PI) / 180)}%`,
                              transform: 'translate(-50%, -50%)',
                              animation: `pulse 2s ease-in-out infinite`,
                              animationDelay: `${i * 0.2}s`
                            }}
                          />
                        ))}

                        {/* Data Streams */}
                        <div className="absolute -left-20 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                          {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map(j => (
                                <div
                                  key={j}
                                  className="w-2 h-2 bg-purple-400/60 rounded-sm"
                                  style={{
                                    animation: 'pulse 1s ease-in-out infinite',
                                    animationDelay: `${(i + j) * 0.1}s`
                                  }}
                                />
                              ))}
                            </div>
                          ))}
                        </div>

                        <div className="absolute -right-20 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                          {[1, 2, 3].map(i => (
                            <div key={i} className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map(j => (
                                <div
                                  key={j}
                                  className="w-2 h-2 bg-cyan-400/60 rounded-sm"
                                  style={{
                                    animation: 'pulse 1s ease-in-out infinite',
                                    animationDelay: `${(i + j) * 0.15}s`
                                  }}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Status Cards */}
                      <div className="absolute top-4 right-4 bg-gray-800/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-purple-500/30 text-xs">
                        <div className="text-purple-400 font-bold">Processing</div>
                        <div className="text-gray-400">1.2M tokens/sec</div>
                      </div>

                      <div className="absolute bottom-4 left-4 bg-gray-800/90 backdrop-blur-sm px-3 py-2 rounded-lg border border-cyan-500/30 text-xs">
                        <div className="text-cyan-400 font-bold">Accuracy</div>
                        <div className="text-gray-400">99.7%</div>
                      </div>

                      {/* Chat Bubble */}
                      <div className="absolute bottom-8 right-1/4 bg-purple-900/80 backdrop-blur-sm px-4 py-2 rounded-2xl rounded-br-sm border border-purple-500/40 animate-bounce-slow">
                        <span className="text-purple-200 text-sm">How can I help? ✨</span>
                      </div>

                      {/* Background Effects */}
                      <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
                      <div className="absolute -z-10 top-1/3 left-1/3 w-32 h-32 bg-pink-500/10 rounded-full blur-2xl animate-pulse" />
                    </div>
                  )}

                  {activeFeature === 2 && ( // Digital Marketing
                    <div className="relative w-full h-full flex items-center justify-center animate-fade-in-up">
                      {/* Analytics Dashboard Mock */}
                      <div className="bg-gray-800/90 rounded-2xl border border-blue-500/30 p-6 max-w-xs shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                            <TrendingUp size={20} className="text-white" />
                          </div>
                          <div>
                            <p className="text-white font-bold">Traffic Growth</p>
                            <p className="text-green-400 text-sm">+247% this month</p>
                          </div>
                        </div>
                        <div className="flex gap-1 h-20 items-end mt-4">
                          {[40, 55, 45, 70, 85, 75, 95].map((h, i) => (
                            <div
                              key={i}
                              className="flex-1 bg-gradient-to-t from-blue-500 to-cyan-400 rounded-t-sm opacity-80"
                              style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between mt-3 text-[10px] text-gray-500">
                          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
                        </div>
                      </div>
                      <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
                    </div>
                  )}

                  {activeFeature === 3 && ( // Creative & Branding
                    <div className="relative w-full h-full flex flex-col items-center justify-center animate-fade-in-up p-2 sm:p-6">
                      {/* Design Tools Grid */}
                      <div className="grid grid-cols-2 gap-3 sm:gap-6 w-full max-w-[160px] sm:max-w-xs mb-8">
                        <div className="bg-gradient-to-br from-orange-500 to-brand-yellow p-3 sm:p-6 rounded-2xl flex items-center justify-center shadow-lg aspect-square">
                          <PenTool className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm p-3 sm:p-6 rounded-2xl flex items-center justify-center border border-white/20 aspect-square text-orange-400">
                          <Layers className="w-8 h-8 sm:w-10 sm:h-10" />
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm p-3 sm:p-6 rounded-2xl flex items-center justify-center border border-white/20 aspect-square text-brand-yellow">
                          <Video className="w-8 h-8 sm:w-10 sm:h-10" />
                        </div>
                        <div className="bg-gradient-to-br from-pink-500 to-purple-500 p-3 sm:p-6 rounded-2xl flex items-center justify-center shadow-lg aspect-square">
                          <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="bg-gray-800/80 backdrop-blur-sm px-5 py-2.5 rounded-full border border-orange-500/30 text-orange-200 text-xs sm:text-sm font-bold tracking-wide">
                        Brand Assets Ready
                      </div>

                      {/* Background Effects */}
                      <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section >

      {/* 5. PROCESS - REDESIGNED */}
      < Section className="relative z-10" >
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent -z-10 hidden md:block" />

        <SectionHeader
          badge="Our Methodology"
          title={<>The <GradientText>NescoTech Method</GradientText></>}
          subtitle="A proven, scientific approach to digital transformation."
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((step, i) => (
            <GlassContainer key={i} className="p-8 relative overflow-hidden group hover:-translate-y-2 transition-transform duration-500 border-white/60 bg-white/60">
              {/* Background Number */}
              <div className="absolute -right-4 -bottom-8 text-9xl font-black text-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 select-none pointer-events-none">
                {step.num}
              </div>

              {/* Connector Dot (Desktop) */}
              <div className="hidden md:block absolute top-1/2 -left-[15px] w-3 h-3 rounded-full bg-white border-2 border-brand-lime z-20" />

              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-lime/10 to-brand-yellow/10 flex items-center justify-center text-brand-dark mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm border border-brand-lime/20">
                  <step.icon size={28} />
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-bold text-brand-lime uppercase tracking-wider bg-brand-lime/10 px-2 py-1 rounded-md">Step {step.num}</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            </GlassContainer>
          ))}
        </div>
      </Section >

      {/* 6. FEATURED WORK - REDESIGNED IMMERSIVE SLIDER */}
      < Section className="relative overflow-visible py-32" >
        {/* Background Aura */}
        < div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-lime/5 rounded-full blur-[100px] -z-10" />

        <div className="flex justify-between items-end mb-16">
          <div className="max-w-2xl">
            <Badge>Selected Work</Badge>
            <h2 className="text-4xl md:text-6xl font-extrabold mt-4">Digital <GradientText>Masterpieces</GradientText></h2>
          </div>
          {/* Slider Controls */}
          <div className="hidden md:flex gap-4">
            <button
              onClick={prevProject}
              aria-label="Previous Project"
              className="w-14 h-14 rounded-full border border-gray-300 flex items-center justify-center hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-300 group"
            >
              <ChevronLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
            </button>
            <button
              onClick={nextProject}
              aria-label="Next Project"
              className="w-14 h-14 rounded-full border border-gray-300 flex items-center justify-center hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-300 group"
            >
              <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        <div className="relative">
          {/* Main Glass Card */}
          {currentProject ? (
            <GlassContainer className="p-0 overflow-hidden min-h-[500px] sm:min-h-[550px] relative border-white/60 shadow-2xl group hover:shadow-brand-lime/10 transition-shadow duration-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 h-full">

                {/* Left Content Area */}
                <div className="p-6 md:p-16 flex flex-col justify-center relative z-10 bg-white/60 backdrop-blur-md">
                  {/* Animated Content Wrapper */}
                  <div key={projectIndex} className="animate-fade-in-up">

                    {/* Tech Stack Icons */}
                    {currentProject.techStack && currentProject.techStack.length > 0 && (
                      <div className="flex flex-wrap gap-4 mb-8">
                        {currentProject.techStack.slice(0, 4).map((tech, idx) => (
                          <div key={idx} className="group/icon relative flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/50 border border-white/50 hover:border-brand-lime/50 transition-colors cursor-help">
                            <Code size={16} className="text-gray-500 group-hover/icon:text-brand-lime transition-colors" />
                            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">{tech}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <h3 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-gray-900 group-hover:to-gray-600 transition-all duration-500">
                      {currentProject.title}
                    </h3>

                    <div className="w-16 h-1.5 bg-brand-lime mb-8 rounded-full" />

                    <p className="text-xl text-gray-500 mb-10 leading-relaxed max-w-md break-words overflow-hidden line-clamp-3">
                      {currentProject.description}
                    </p>

                    <div className="flex items-center gap-6">
                      <PrimaryButton onClick={() => navigate('/portfolio')} className="shadow-xl">
                        View Case Study
                      </PrimaryButton>
                      <button className="flex items-center gap-2 font-bold text-gray-900 hover:text-brand-lime transition-colors group/link">
                        Live Preview <ArrowUpRight size={18} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                      </button>
                    </div>
                  </div>

                  {/* Mobile Slider Controls */}
                  <div className="flex md:hidden items-center justify-center gap-6 mt-12 mb-4">
                    <button
                      onClick={prevProject}
                      aria-label="Previous Project"
                      className="w-14 h-14 rounded-full border border-gray-200 flex items-center justify-center bg-white/50 backdrop-blur-sm active:bg-brand-dark active:text-white transition-all shadow-sm"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextProject}
                      aria-label="Next Project"
                      className="w-14 h-14 rounded-full border border-gray-200 flex items-center justify-center bg-white/50 backdrop-blur-sm active:bg-brand-dark active:text-white transition-all shadow-sm"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                </div>

                {/* Right Image Area */}
                <div className="relative h-[300px] md:h-auto overflow-hidden">
                  <div className="absolute inset-0 bg-gray-900" />

                  {/* Image with Ken Burns Effect */}
                  <OptimizedImage
                    key={projectIndex}
                    src={currentProject.image}
                    alt={currentProject.title}
                    width={1200}
                    height={800}
                    priority={true}
                    className="w-full h-full object-cover opacity-90 animate-[scale-in_1.5s_ease-out_forwards] [backface-visibility:hidden] [transform:translateZ(0)]"
                  />

                  {/* Gradient Overlay for Text Readability/Aesthetic */}
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-white/10 mix-blend-overlay" />

                  {/* Glass Badge Overlay */}
                  <div className="absolute bottom-10 right-10">
                    <div className="bg-black/30 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 text-white font-mono text-sm shadow-lg flex items-center gap-3">
                      <span className="w-2 h-2 bg-brand-lime rounded-full animate-pulse" />
                      <span>0{projectIndex + 1} / 0{projects.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </GlassContainer>
          ) : (
            <GlassContainer className="p-16 text-center min-h-[400px] flex items-center justify-center">
              <div>
                <div className="animate-spin w-10 h-10 border-4 border-brand-lime border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Loading portfolio...</p>
              </div>
            </GlassContainer>
          )}
        </div>
      </Section >

      {/* 7. TESTIMONIALS SLIDER - REDESIGNED */}
      < Section className="relative py-16 md:py-20 overflow-hidden" >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left Control Column */}
          <div className="relative z-10 text-center lg:text-left">
            <Badge>Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mt-4 mb-4 md:mb-6 leading-tight">
              Trusted by <br className="hidden lg:block" /><GradientText>Visionaries</GradientText>
            </h2>
            <p className="text-base md:text-lg text-gray-500 mb-6 md:mb-8 max-w-md mx-auto lg:mx-0">
              We don't just build software; we build long-term partnerships. Here is what our partners have to say.
            </p>

            <div className="flex items-center justify-center lg:justify-start gap-3">
              <button
                onClick={prevTestimonial}
                aria-label="Previous Testimonial"
                className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-brand-lime hover:text-white hover:border-brand-lime transition-all duration-300"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextTestimonial}
                aria-label="Next Testimonial"
                className="w-10 h-10 md:w-12 md:h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-brand-lime hover:text-white hover:border-brand-lime transition-all duration-300"
              >
                <ChevronRight size={20} />
              </button>
              <div className="ml-4 flex gap-1.5">
                {testimonials.map((_, idx) => (
                  <div
                    key={idx}
                    onClick={() => setTestimonialIndex(idx)}
                    aria-label={`Go to testimonial ${idx + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-500 cursor-pointer ${idx === testimonialIndex ? 'w-6 bg-brand-lime' : 'w-1.5 bg-gray-300'}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Slider Column */}
          <div className="relative min-h-[280px] md:min-h-[320px]">
            {/* Background Decor */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-brand-lime/10 to-brand-yellow/10 rounded-full blur-[80px] -z-10" />

            {testimonials.map((testimonial, idx) => (
              <div
                key={testimonial.id}
                className={`
                        absolute inset-0 transition-all duration-700 ease-out transform
                        ${idx === testimonialIndex ? 'opacity-100 translate-y-0 z-10' : 'opacity-0 translate-y-10 z-0 pointer-events-none'}
                     `}
              >
                <GlassContainer className="p-6 md:p-8 h-full flex flex-col justify-between border-white/60 shadow-xl">
                  <div>
                    <Quote size={28} className="text-brand-lime/30 mb-3" />
                    <p className="text-base md:text-lg font-medium text-gray-700 leading-relaxed line-clamp-4 break-words">
                      "{testimonial.content}"
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                    <OptimizedAvatar
                      src={testimonial.avatar || testimonial.image || `https://i.pravatar.cc/100?u=${testimonial.id}`}
                      alt={testimonial.name}
                      width={32}
                      height={32}
                      className="border-2 border-white shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-sm md:text-base text-gray-900 truncate">{testimonial.name}</h3>
                      <p className="text-xs text-brand-lime font-bold uppercase tracking-wider truncate">{testimonial.role} {testimonial.company ? `at ${testimonial.company}` : ''}</p>
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} size={12} className={`${s < testimonial.rating ? 'fill-brand-yellow text-brand-yellow' : 'fill-gray-200 text-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                </GlassContainer>
              </div>
            ))}
            {testimonials.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-400 italic text-sm">
                Loading testimonials...
              </div>
            )}
          </div>
        </div>
      </Section >

      {/* 8. FAQ */}
      < Section >
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <Badge>FAQ</Badge>
            <h2 className="text-4xl font-bold mt-4 mb-6">Common Questions</h2>
            <p className="text-gray-500 text-lg mb-8">
              Everything you need to know about working with a top-tier digital agency.
            </p>
            <PrimaryButton onClick={() => navigate('/booking')}>Ask Something Else</PrimaryButton>
          </div>
          <div>
            <Accordion items={FAQS} />
          </div>
        </div>
      </Section >

      {/* 9. CTA */}
      < section className="py-32 px-6" >
        <GlassContainer className="max-w-7xl mx-auto p-12 md:p-24 text-center relative overflow-hidden bg-gradient-to-br from-brand-lime to-brand-yellow border-none">
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-8">Ready to dominate <br />your market?</h2>
            <p className="text-white/90 text-xl max-w-2xl mx-auto mb-12">
              Stop settling for average. Let's build a digital experience that converts, scales, and impresses.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <ShineButton onClick={() => navigate('/booking')} className="!bg-brand-dark !text-white !bg-none shadow-2xl ring-1 ring-white/10 hover:scale-105">
                Get Your Free Quote
              </ShineButton>
              <button onClick={() => navigate('/portfolio')} className="px-10 py-5 bg-transparent border-2 border-white text-white font-bold rounded-full hover:bg-white/10 transition-colors">
                View Portfolio
              </button>
            </div>
          </div>
          {/* Decorative Circles */}
          <div className="absolute top-0 left-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
        </GlassContainer>
      </section >
    </>
  );
};
